import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatMetric } from './panelConfig';

const BUCKETS = {
  '<30': { label: '≤30 días', color: '#10b981' },
  '30-60': { label: '30–60', color: '#84cc16' },
  '60-90': { label: '60–90', color: '#eab308' },
  '90-180': { label: '90–180', color: '#f97316' },
  '180+': { label: '+180', color: '#ef4444' },
  nunca: { label: 'Nunca', color: '#6b7280' },
};

function Card({ label, value, hint, danger }) {
  return (
    <div className="rounded-md bg-gray-50 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${danger ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function daysAgo(iso) {
  if (!iso) return null;
  return Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
}


export default function CatalogHealth({ health, isLoading, isError }) {
  const [metric, setMetric] = useState('value'); // 'value' | 'skus'
  const [showAllDead, setShowAllDead] = useState(false);
  const [showAllOver, setShowAllOver] = useState(false);

  const summary = health?.summary;
  const agingData = useMemo(
    () =>
      (health?.aging ?? []).map((a) => ({
        ...a,
        label: BUCKETS[a.bucket]?.label ?? a.bucket,
        color: BUCKETS[a.bucket]?.color ?? '#9ca3af',
      })),
    [health]
  );
  const catData = useMemo(
    () =>
      (health?.by_category ?? []).map((c) => ({
        ...c,
        name: c.category || 'Sin categoría',
      })),
    [health]
  );

  if (isLoading) return <p className="py-8 text-center text-sm text-gray-500">Cargando salud del catálogo…</p>;
  if (isError) return null;
  if (!summary || !summary.in_stock_skus) return null;

  const fmtMoney = (v) => formatMetric(v ?? 0, 'currency');
  const fmtNum = (v) => formatMetric(v ?? 0, 'number');
  const fmtPct = (v) => formatMetric(v, 'percent');
  const isMoney = metric === 'value';

  return (
    <section className="space-y-5 rounded-xl border border-gray-200 bg-white p-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Stock inmovilizado</h2>
        <p className="text-sm text-gray-500">
          De los SKUs que tenés en stock hoy, cuánta plata está parada. "Clavado" = no vende hace
          +180 días (o nunca). Ventanas fijas hacia atrás desde hoy — no dependen del período del reporte.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card
          label="Valor inmovilizado"
          value={fmtMoney(summary.immobilized_value)}
          hint={`${fmtPct(summary.immobilized_value_pct)} del valor del stock`}
          danger
        />
        <Card
          label="SKUs clavados (+180d)"
          value={fmtNum(summary.immobilized_skus)}
          hint={`de ${fmtNum(summary.in_stock_skus)} en stock · ${summary.never_sold_skus} nunca vendieron`}
        />
        <Card
          label="Valor del stock"
          value={fmtMoney(summary.in_stock_value)}
          hint={`${fmtNum(summary.in_stock_skus)} SKUs disponibles`}
        />
        <Card
          label="Sobrestock"
          value={fmtNum(summary.overstock_skus)}
          hint="venden, pero tenés +180 días de stock"
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Distribución por antigüedad de venta */}
        <div className="rounded-lg border border-gray-100 p-4">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Antigüedad de la última venta</h3>
            <div className="flex overflow-hidden rounded-md border border-gray-200 text-xs">
              <button
                type="button"
                onClick={() => setMetric('value')}
                className={`px-2.5 py-1 ${isMoney ? 'bg-gray-900 text-white' : 'text-gray-500'}`}
              >
                $ valor
              </button>
              <button
                type="button"
                onClick={() => setMetric('skus')}
                className={`px-2.5 py-1 ${!isMoney ? 'bg-gray-900 text-white' : 'text-gray-500'}`}
              >
                # SKUs
              </button>
            </div>
          </div>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={agingData} margin={{ top: 12, right: 8, left: -12, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} width={44} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: '#f8fafc' }}
                  formatter={(v) => [isMoney ? fmtMoney(v) : fmtNum(v), isMoney ? 'Valor' : 'SKUs']}
                />
                <Bar dataKey={isMoney ? 'value' : 'skus'} radius={[3, 3, 0, 0]}>
                  {agingData.map((d) => (
                    <Cell key={d.bucket} fill={d.color} />
                  ))}
                  <LabelList
                    dataKey={isMoney ? 'value' : 'skus'}
                    position="top"
                    formatter={(v) => (isMoney ? formatMetric(v, 'currency') : v)}
                    style={{ fontSize: 10, fill: '#64748b' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Plata clavada por categoría */}
        <div className="rounded-lg border border-gray-100 p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Plata clavada por categoría</h3>
          {catData.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-400">Sin clavados por categoría.</p>
          ) : (
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={catData}
                  layout="vertical"
                  margin={{ top: 0, right: 40, left: 8, bottom: 0 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    width={110}
                  />
                  <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(v) => [fmtMoney(v), 'Valor clavado']} />
                  <Bar dataKey="value" fill="#ef4444" radius={[0, 3, 3, 0]}>
                    <LabelList
                      dataKey="value"
                      position="right"
                      formatter={(v) => formatMetric(v, 'currency')}
                      style={{ fontSize: 10, fill: '#64748b' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Tabla de clavados */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-900">Productos clavados (+180d), por plata parada</h3>
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead className="bg-white text-xs uppercase tracking-wide text-gray-500">
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-medium">Producto</th>
                  <th className="px-3 py-2 text-right font-medium">Stock</th>
                  <th className="px-3 py-2 text-right font-medium">Últ. venta</th>
                  <th className="px-3 py-2 text-right font-medium">Días</th>
                  <th className="px-3 py-2 text-right font-medium">Valor clavado</th>
                </tr>
              </thead>
              <tbody>
                {(showAllDead ? (health.dead_stock ?? []) : (health.dead_stock ?? []).slice(0, 10)).map((d) => (
                  <tr key={d.sku} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                    <td className="px-3 py-2.5">
                      <div className="text-gray-900">{d.product_name || d.sku}</div>
                      <div className="text-xs text-gray-400">
                        {d.sku}
                        {d.category ? ` · ${d.category}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtNum(d.stock)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {d.last_sale ? d.last_sale : 'nunca'}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {d.days_since_sale ?? (d.last_sale ? daysAgo(d.last_sale) : '—')}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-medium text-red-600">
                      {fmtMoney(d.value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(health.dead_stock ?? []).length > 10 && (
            <button
              type="button"
              onClick={() => setShowAllDead((v) => !v)}
              className="w-full border-t border-gray-100 py-2 text-sm font-medium text-[#1B3DFB] hover:bg-gray-50"
            >
              {showAllDead ? 'Ver menos' : `Ver todos (${(health.dead_stock ?? []).length})`}
            </button>
          )}
        </div>
      </div>

      {/* Sobrestock (rota lento pero tenés de más) */}
      {(health.overstock ?? []).length > 0 && (
        <div>
          <h3 className="mb-1 text-sm font-semibold text-gray-900">Sobrestock — venden, pero tenés de más</h3>
          <p className="mb-2 text-xs text-gray-500">
            Días de stock = unidades en stock ÷ velocidad de venta. +180 días = comprado de más.
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-collapse text-sm">
                <thead className="bg-white text-xs uppercase tracking-wide text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-medium">Producto</th>
                    <th className="px-3 py-2 text-right font-medium">Stock</th>
                    <th className="px-3 py-2 text-right font-medium">U/mes</th>
                    <th className="px-3 py-2 text-right font-medium">Días de stock</th>
                    <th className="px-3 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllOver ? health.overstock : health.overstock.slice(0, 10)).map((o) => (
                    <tr key={o.sku} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2.5">
                        <div className="text-gray-900">{o.product_name || o.sku}</div>
                        <div className="text-xs text-gray-400">{o.sku}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtNum(o.stock)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-500">{o.units_per_month}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-medium text-orange-600">
                        {fmtNum(o.days_of_stock)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{fmtMoney(o.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {health.overstock.length > 10 && (
              <button
                type="button"
                onClick={() => setShowAllOver((v) => !v)}
                className="w-full border-t border-gray-100 py-2 text-sm font-medium text-[#1B3DFB] hover:bg-gray-50"
              >
                {showAllOver ? 'Ver menos' : `Ver todos (${health.overstock.length})`}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
